import { TrackJS } from 'trackjs';
import {
  BroadcastChannelMessage,
  GraphTokens,
  SessionTokens,
} from './types';

const CHANNEL_NAME = 'tt-auth-coordination';
const CLAIM_WINDOW_MS = 50;
const WAIT_FOR_PEER_MS = 5000;

type ClaimKind = 'sso' | 'graph';

interface ClaimSsoOpts {
  kind: 'sso';
  oid: string;
  scopes: string[];
}

interface ClaimGraphOpts {
  kind: 'graph';
  oid: string;
  scopeKey: string;
}

export type ClaimOpts = ClaimSsoOpts | ClaimGraphOpts;

type ClaimResult<T extends ClaimKind> =
  | { kind: 'proceed'; nonce: string }
  | { kind: 'await'; tokens: T extends 'sso' ? SessionTokens : GraphTokens };

function generateNonce(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export class TabCoordinator {
  private channel: BroadcastChannel | null;
  private readonly tabId: string = generateNonce();
  private readonly supported: boolean;

  constructor() {
    this.supported = typeof BroadcastChannel !== 'undefined';
    this.channel = this.supported ? new BroadcastChannel(CHANNEL_NAME) : null;
  }

  isSupported(): boolean {
    return this.supported;
  }

  /**
   * Returns:
   *  - { kind: 'proceed', nonce } when caller should proceed with the acquisition.
   *  - { kind: 'await', tokens } when a peer has finished and shared the result.
   * Returns 'proceed' if BroadcastChannel is unsupported, no peer is acquiring,
   * or this tab's nonce sorts before the peer's.
   */
  async claim<T extends ClaimKind>(opts: ClaimOpts & { kind: T }): Promise<ClaimResult<T>> {
    const ownNonce = generateNonce();

    if (!this.channel) {
      return { kind: 'proceed', nonce: ownNonce };
    }

    const ownAcquireMessage = this.buildAcquireMessage(opts, ownNonce);
    this.channel.postMessage(ownAcquireMessage);

    const peerNonce = await this.listenForPeerClaim(opts, ownNonce);

    if (peerNonce === null) {
      return { kind: 'proceed', nonce: ownNonce };
    }

    if (ownNonce < peerNonce) {
      return { kind: 'proceed', nonce: ownNonce };
    }

    const tokens = await this.waitForPeerResult<T>(opts, peerNonce);
    if (!tokens) {
      return { kind: 'proceed', nonce: ownNonce };
    }
    return { kind: 'await', tokens } as ClaimResult<T>;
  }

  publishResult(kind: ClaimKind, nonce: string, tokens: SessionTokens | GraphTokens): void {
    if (!this.channel) return;
    if (kind === 'sso') {
      this.channel.postMessage({ type: 'sso-result', nonce, tokens: tokens as SessionTokens });
    } else {
      this.channel.postMessage({ type: 'graph-result', nonce, tokens: tokens as GraphTokens });
    }
  }

  publishFailure(kind: ClaimKind, nonce: string, errorCode?: string): void {
    if (!this.channel) return;
    if (kind === 'sso') {
      this.channel.postMessage({ type: 'sso-failed', nonce, errorCode });
    } else {
      this.channel.postMessage({ type: 'graph-failed', nonce, errorCode });
    }
  }

  dispose(): void {
    this.channel?.close();
    this.channel = null;
  }

  private buildAcquireMessage(opts: ClaimOpts, nonce: string): BroadcastChannelMessage {
    if (opts.kind === 'sso') {
      return {
        type: 'sso-acquiring',
        oid: opts.oid,
        scopes: opts.scopes,
        tabId: this.tabId,
        nonce,
      };
    }
    return {
      type: 'graph-acquiring',
      oid: opts.oid,
      scopeKey: opts.scopeKey,
      tabId: this.tabId,
      nonce,
    };
  }

  private listenForPeerClaim(opts: ClaimOpts, ownNonce: string): Promise<string | null> {
    return new Promise((resolve) => {
      const handler = (ev: MessageEvent<BroadcastChannelMessage>) => {
        const msg = ev.data;
        if (!this.matchesAcquire(msg, opts)) return;
        if (msg.nonce === ownNonce) return;
        this.channel?.removeEventListener('message', handler);
        clearTimeout(timer);
        resolve(msg.nonce);
      };
      this.channel?.addEventListener('message', handler);
      const timer = setTimeout(() => {
        this.channel?.removeEventListener('message', handler);
        resolve(null);
      }, CLAIM_WINDOW_MS);
    });
  }

  private waitForPeerResult<T extends ClaimKind>(
    opts: ClaimOpts,
    peerNonce: string,
  ): Promise<(T extends 'sso' ? SessionTokens : GraphTokens) | null> {
    return new Promise((resolve) => {
      const handler = (ev: MessageEvent<BroadcastChannelMessage>) => {
        const msg = ev.data;
        if (opts.kind === 'sso' && msg.type === 'sso-result' && msg.nonce === peerNonce) {
          this.channel?.removeEventListener('message', handler);
          clearTimeout(timer);
          resolve(msg.tokens as never);
          return;
        }
        if (opts.kind === 'graph' && msg.type === 'graph-result' && msg.nonce === peerNonce) {
          this.channel?.removeEventListener('message', handler);
          clearTimeout(timer);
          resolve(msg.tokens as never);
          return;
        }
        if (opts.kind === 'sso' && msg.type === 'sso-failed' && msg.nonce === peerNonce) {
          this.channel?.removeEventListener('message', handler);
          clearTimeout(timer);
          resolve(null);
          return;
        }
        if (opts.kind === 'graph' && msg.type === 'graph-failed' && msg.nonce === peerNonce) {
          this.channel?.removeEventListener('message', handler);
          clearTimeout(timer);
          resolve(null);
          return;
        }
      };
      this.channel?.addEventListener('message', handler);
      const timer = setTimeout(() => {
        this.channel?.removeEventListener('message', handler);
        TrackJS?.track('[tabCoord] timeout waiting for peer; proceeding');
        resolve(null);
      }, WAIT_FOR_PEER_MS);
    });
  }

  private matchesAcquire(
    msg: BroadcastChannelMessage,
    opts: ClaimOpts,
  ): msg is
    | Extract<BroadcastChannelMessage, { type: 'sso-acquiring' }>
    | Extract<BroadcastChannelMessage, { type: 'graph-acquiring' }> {
    if (opts.kind === 'sso') {
      return (
        msg.type === 'sso-acquiring' &&
        msg.oid === opts.oid &&
        sameScopes(msg.scopes, opts.scopes)
      );
    }
    return (
      msg.type === 'graph-acquiring' &&
      msg.oid === opts.oid &&
      msg.scopeKey === opts.scopeKey
    );
  }
}

function sameScopes(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sa = a.slice().sort();
  const sb = b.slice().sort();
  for (let i = 0; i < sa.length; i++) {
    if (sa[i] !== sb[i]) return false;
  }
  return true;
}
